import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { api } from "@tripian/core";
import moment from "moment";
import { getTranslationList } from "../AppWrapper/init";

const changeMomentLocale = async (newLocale: string) => {
  try {
    const localeCode = newLocale === "en" ? "en-gb" : newLocale;
    await import(`moment/locale/${localeCode}`);
    // Bazı locale'ler (ör. Arapça) preparse/postformat ile rakamları yerel
    // rakamlara çevirir; bu, API'ye giden tarihleri ve YYYY-MM-DD string
    // karşılaştırmalarını bozar. Rakamlar her zaman Latin (0-9) kalmalı.
    moment.updateLocale(localeCode, {
      preparse: (str: string) => str,
      postformat: (str: string) => str,
    });
    moment.locale(localeCode);
  } catch (error) {
    console.error(`Failed to load locale file for ${newLocale}:`, error);
  }
};

const RTL_LANGUAGES = ["ar", "he", "fa", "ur"];

type LanguageContextType = {
  langCode: string;
  onSelectedLangCode: (langCode: string) => void;
};

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { getLocalData } = useLocalStorage<string>("language", true);

  const [langCode, setLangCode] = useState<string>(getLocalData() || "en");

  useEffect(() => {
    changeMomentLocale(langCode);
    document.documentElement.lang = langCode;
    document.documentElement.dir = RTL_LANGUAGES.includes(langCode)
      ? "rtl"
      : "ltr";
  }, [langCode]);

  const onSelectedLangCode = async (langCode: string) => {
    if (!window.tconfig.T.translations[langCode]) {
      try {
        const result = await getTranslationList(langCode);
        window.tconfig.T.translations[langCode] = result.translations[langCode];
      } catch (error) {
        console.error(`getTranslationList(${langCode}).error`, error);
        return;
      }
    }

    localStorage.setItem("language", langCode);
    await changeMomentLocale(langCode);
    window.twindow.langCode = langCode;
    setLangCode(langCode);
    api.setLang(langCode);

    // Redux state'i sıfırla
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: langCode }));
  };

  const value: LanguageContextType = {
    langCode,
    onSelectedLangCode,
  };

  return (
    <LanguageContext.Provider value={value}>
      <React.Fragment>{children}</React.Fragment>
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
